/**
 * Events + Clubs redesign — design tokens (docs/plans/2026-09-events-clubs-redesign/handoff/README.md).
 * Hex values here are the ones the handoff names; map to Tailwind (`border-2 border-black`,
 * `text-hand-muted`) wherever a utility already exists, and reach for these only for
 * inline style values the utilities cannot express.
 */
export const MUTED = '#6B7280';
export const SUBTLE = '#9CA3AF';
export const HAIRLINE = '#E5E7EB';
export const INSET = '#FAFAFA';
export const BODY_TEXT = '#374151';
export const GREEN = '#15803D';
export const DANGER = '#DC2626';
export const WARNING = '#EAB308';
export const WARNING_BG = '#FEFCE8';
export const WARNING_TEXT = '#713F12';
export const AMBER_TEXT = '#B45309';
export const DANGER_BG = '#FEF2F2';
export const DANGER_TEXT = '#7F1D1D';
export const PINNED_BG = '#FFF8EA';
export const CYAN = '#03FFFF';
export const MAGENTA = '#FF00FF';
export const GLITCH_GREEN = '#03FF00';

/** Disco accent palette — punctuation only (chips, pills, poster placeholders). Never a whole surface. */
export const DISCO = {
  cyan: '#03FFFF', magenta: '#FF00FF', green: '#03FF00', yellow: '#FAF42E', amber: '#F9B82C',
  mint: '#9EE7D7', sky: '#73C6E8', blue: '#25B6E2', pink: '#E462A5', rose: '#E696C5',
  lilac: '#C6B8FF', lime: '#A2E580', blush: '#FFCCE6', powder: '#A8D8EA',
};

/**
 * The header fade. Copied stop-for-stop from the handoff (it matches ProHeader's
 * vocabulary). Confined to MosaicHeader — do not reuse on other chrome.
 */
export const FADE = 'linear-gradient(90deg, rgba(255,255,255,.93) 1%, rgba(255,255,255,.85) 36%, rgba(255,255,255,.72) 46%, rgba(255,255,255,.55) 55%, rgba(255,255,255,0) 63%)';

/** Sticky offsets, desktop: TopBar 68 → optional status band at 68 → rails at 92. */
export const TOPBAR_DESKTOP = 68;
export const BAND_TOP = 68;
export const RAIL_TOP = 92;
/** Mobile: the collapsing title bar is measured at runtime; this is the fallback. */
export const MOBILE_TITLE_BAR = 49;
/** Global mobile tab bar (MobileTabBar.jsx) — a floating 58px capsule 10px off the bottom, + safe-area inset. */
export const MOBILE_TAB_BAR = 68;

export const SERIF = 'Times, "Times New Roman", serif';

/** Type roles as inline-style objects, for the few sizes Tailwind has no exact utility for. */
export const TYPE = {
  pageTitle: { fontSize: 42, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.06, overflowWrap: 'anywhere' },
  pageTitleMobile: { fontSize: 20, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.15 },
  clubName: { fontSize: 38, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.06, overflowWrap: 'anywhere' },
  sectionTitle: { fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' },
  bandLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' },
  bandNote: { fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: SUBTLE },
  fieldLabel: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: MUTED },
  eyebrow: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.2em', color: MUTED },
  body: { fontSize: 13, lineHeight: 1.6 },
  meta: { fontSize: 11, color: MUTED },
  price: { fontSize: 32, fontWeight: 700, lineHeight: 1 },
  vibe: { fontFamily: SERIF, fontStyle: 'italic', fontSize: 15, lineHeight: 1.45, color: BODY_TEXT },
};

/** Motion: 200ms color transitions on hover, nothing else. Sheets slide 200ms on this curve. */
export const SHEET_EASE = 'cubic-bezier(.2,.7,.3,1)';

export function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}
